<script setup>
import ReferralAction from "./ReferralAction";
import ReferralStatus from "../../../common/ReferralStatus";
import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import {computed} from "vue";
import EditableTextField from "../../portal-editable-fields/EditableTextField.vue";

    const props = defineProps({
        recentActivities: {
            type: Array,
            default: () => []
        }
    })

    const portalContentAndDesignStore = usePortalContentAndDesignStore();

    const layoutStyle = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style);

    const getMultiTierLabel = (referral) => {
        const level = referral['multi_tier_level'];
        const baseLabel = portalContentAndDesignStore.translationSettings.portal_translation_general_multi_tier_commission || 'Multi-level commission';

        if (level && level >= 1) {
            return `${baseLabel} (Level ${level})`;
        }

        return baseLabel;
    };

</script>

<template>
    <table
        class="table"
        :class="{ 'table-wide': layoutStyle === 'none'}"
    >
        <thead>
        <tr>
            <th>
                <editable-text-field
                    text-type="span"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_referral"
                ></editable-text-field>
            </th>
            <th>
                <editable-text-field
                    text-type="span"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_action"
                ></editable-text-field>
            </th>
            <th>
                <editable-text-field
                    text-type="span"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_reward"
                ></editable-text-field>
            </th>
            <th>
                <editable-text-field
                    text-type="span"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_status"
                ></editable-text-field>
            </th>
            <th>
                <div class="flex justify-end">
                    <editable-text-field
                        text-type="span"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_date"
                    ></editable-text-field>
                </div>
            </th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="referral in recentActivities"
            :key="`${referral.resource_type}-${referral.id}`"
            class="hover:bg-gray-100 hover:bg-opacity-50"
        >
            <td class="whitespace-nowrap">
                <div v-if="referral['multi_tier_affiliate_commission']"
                     class="font-medium"
                >
                    {{ getMultiTierLabel(referral) }}
                    <span v-if="portalContentAndDesignStore.portalLayoutSettings.portal_general_show_referred_partners_table && (referral['referred_partner_name'] || referral['referred_partner_email'])"
                          class="block text-sm opacity-60 font-normal"
                    >
                        {{ portalContentAndDesignStore.translationSettings.portal_translation_general_by || 'By' }} {{ referral['referred_partner_name'] || referral['referred_partner_email'] }}<template v-if="referral['referred_partner_name'] && referral['referred_partner_email']"> ({{ referral['referred_partner_email'] }})</template>
                    </span>
                </div>
                <router-link
                    v-else-if="referral['customer_id']"
                    :to="`/referral/${referral['customer_id']}`"
                    class="block group"
                >
                    <template v-if="referral['customer_full_name']">
                        <div
                            class="font-medium group-hover:underline"
                            :class="`group-hover:${portalContentAndDesignStore.getLinkColorClass}`"
                        >
                            {{ referral['customer_full_name'] }}
                        </div>
                    </template>
                    <template v-else-if="portalContentAndDesignStore.portalLayoutSettings.portal_general_hide_partner_contact_info">
                        <div
                            class="font-medium italic opacity-60 group-hover:underline"
                            :class="`group-hover:${portalContentAndDesignStore.getLinkColorClass}`"
                        >
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_general_referral_name_not_provided || 'Referral (name not provided)' }}
                        </div>
                    </template>
                    <template v-else-if="referral['email']">
                        <div
                            class="font-medium group-hover:underline"
                            :class="`group-hover:${portalContentAndDesignStore.getLinkColorClass}`"
                        >
                            {{ referral['email'] }}
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="font-medium italic opacity-60 group-hover:underline"
                            :class="`group-hover:${portalContentAndDesignStore.getLinkColorClass}`"
                        >
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_general_referral_name_not_provided || 'Referral (name not provided)' }}
                        </div>
                    </template>
                    <span v-if="!portalContentAndDesignStore.portalLayoutSettings.portal_general_hide_partner_contact_info && referral['email'] && referral['customer_full_name']"
                          class="opacity-60"
                    >
                        {{ referral['email'] }}
                    </span>
                </router-link>
                <template v-else>-</template>
            </td>


            <td class="whitespace-nowrap">
                <referral-action :action="referral['action']" :text-color="portalContentAndDesignStore.getTextColorClass"/>
            </td>
            <td class="font-medium whitespace-nowrap">
                <tooltip v-if="referral['reward_commission_description']" :content="referral['reward_commission_description']" class="cursor-pointer">
                    {{ referral['currency_amount'] || ' - ' }}
                </tooltip>
                <div v-else>
                    {{ referral['currency_amount'] || ' - ' }}
                </div>
            </td>
            <td class="font-medium whitespace-nowrap">
                <referral-status :status="referral['status']" :archived="referral['is_archived']" :activity="referral" :translations="portalContentAndDesignStore.translationSettings" />
            </td>
            <td class="whitespace-nowrap text-right">
                <time
                    class="opacity-60"
                    :datetime="referral['date']"
                >
                    {{ referral['parsed_date_time'] }}
                </time>
            </td>
        </tr>
        </tbody>
    </table>
</template>
